import { Button } from '@lifeomic/chroma-react/components/Button';
import { Controller, useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { SavedFilter } from '../../types';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { Toggle } from '@lifeomic/chroma-react/components/Toggle';
import { useABAC } from '@lifeomic/phc-web-toolkit/src/hooks/useABAC';
import { useActiveUserId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveUserId';
import * as React from 'react';

const messages = defineMessages({
  modalTitle: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.modalTitle',
    defaultMessage: 'Filter Settings',
  },
  nameLabel: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.nameLabel',
    defaultMessage: 'Name',
  },
  nameLabelRequired: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.nameLabelRequired',
    defaultMessage: 'A filter name is required.',
  },
  description: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.description',
    defaultMessage: 'Description',
  },
  createAsNewFilter: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.createAsNewFilter',
    defaultMessage: 'Create as a new saved filter',
  },
  setAsDefaultFilter: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.setAsDefaultFilter',
    defaultMessage: 'Set as default filter',
  },
  setAsDefaultFilterHelp: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.setAsDefaultFilterHelp',
    defaultMessage:
      'A single filter to apply automatically for the selected sequence and variant combination (e.g., Somatic + Short). Applies to the whole project. To remove a default filter, the filter must be deleted or replaced by a new filter.',
  },
  filterNameCopy: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.filterNameCopy',
    defaultMessage: '{name} (Copy)',
  },
  owner: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.owner',
    defaultMessage: 'Filter Owner',
  },
  ownerHelpMessage: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.ownerHelpMessage',
    defaultMessage:
      'Only the filter owner or a project admin can update this filter.',
  },
  cancel: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'omicsExplorer.savedFilters.createOrUpdateFilterModal.saveFilterModal.save',
    defaultMessage: 'Save',
  },
});

interface SaveFilterModalProps {
  isDefaultFilterAlready?: boolean;
  isOpen?: boolean;
  onCreateNewFilter?: (filterFormValues: FormValues) => any;
  onUpdateFilter?: (filter: SavedFilter, setAsDefaultFilter: boolean) => any;
  onDismiss?: () => void;
  savedFilter?: SavedFilter;
}

export interface FormValues {
  createAsNewFilter?: boolean;
  description?: string;
  name: string;
  setAsDefaultFilter?: boolean;
}

export const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  isDefaultFilterAlready = false,
  isOpen,
  onCreateNewFilter,
  onDismiss,
  onUpdateFilter,
  savedFilter,
  ...rootProps
}) => {
  const { formatMessage } = useIntl();
  const { register, handleSubmit, errors, control, watch } =
    useForm<FormValues>();

  const createAsNewFilter = watch('createAsNewFilter');

  const [canPerform, permissions] = useABAC();
  const activeUserId = useActiveUserId();

  const getButtonDisabledState = () => {
    // We require at least one of these permissions to save a filter
    if (
      [
        canPerform(permissions.readData),
        canPerform(permissions.readMaskedData),
        canPerform(permissions.projectAdmin),
      ].every(permission => permission === false)
    ) {
      return true;
    }

    // We're looking at an existing filter, but want to clone it
    if (savedFilter && createAsNewFilter) {
      return false;
    }

    // We're looking at an existing filter, but NOT cloning, updating it
    // ...for that we require the user to be the owner of the filter *OR*
    //    the user having the projectAdmin permission
    if (
      savedFilter &&
      !(
        savedFilter?.owner === activeUserId ||
        canPerform(permissions.projectAdmin)
      )
    ) {
      return true;
    }

    return false;
  };

  const handleFormSubmit = async (formValues: FormValues) => {
    // We are creating a new filter
    if (!savedFilter) {
      onCreateNewFilter?.(formValues);
      return;
    }

    // We are cloning an existing filter
    if (savedFilter && formValues?.createAsNewFilter) {
      const clonedFilter: FormValues = {
        name:
          formValues.name === savedFilter.name
            ? formatMessage(messages.filterNameCopy, {
                name: savedFilter.name,
              })
            : formValues.name,
        description: formValues.description,
        setAsDefaultFilter: formValues.setAsDefaultFilter,
      };

      onCreateNewFilter?.(clonedFilter);
      return;
    }

    // We are updating an existing filter
    const updatedFilter: SavedFilter = {
      id: savedFilter.id,
      name: formValues.name,
      description: formValues.description,
    };
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    onUpdateFilter?.(updatedFilter, formValues.setAsDefaultFilter);
  };

  return (
    <Modal
      isFormContent={true}
      isOpen={isOpen}
      onDismiss={onDismiss}
      onFormSubmit={handleSubmit(handleFormSubmit)}
      size={1}
      title={formatMessage(messages.modalTitle)}
      actions={
        <>
          <Button variant="text" onClick={onDismiss}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={getButtonDisabledState()}
          >
            {formatMessage(messages.save)}
          </Button>
        </>
      }
      {...rootProps}
    >
      <FormBox>
        <TextField
          name="name"
          ref={register({ required: true })}
          label={formatMessage(messages.nameLabel)}
          hasError={!!errors.name}
          errorMessage={formatMessage(messages.nameLabelRequired)}
          defaultValue={savedFilter?.name}
          fullWidth
        />

        <TextArea
          name="description"
          ref={register({ required: false })}
          label={formatMessage(messages.description)}
          defaultValue={savedFilter?.description}
          fullWidth
        />

        {savedFilter && savedFilter?.owner !== activeUserId && (
          <TextField
            name="owner"
            ref={register({})}
            label={formatMessage(messages.owner)}
            helpMessage={formatMessage(messages.ownerHelpMessage)}
            defaultValue={savedFilter?.owner}
            readOnly
            fullWidth
          />
        )}

        {savedFilter && (
          <Controller
            as={
              <Toggle
                label={formatMessage(messages.createAsNewFilter)}
                name="createAsNewFilter"
              />
            }
            control={control}
            defaultValue={false}
            name="createAsNewFilter"
            value="createAsNewFilter"
          />
        )}

        <Controller
          as={
            <Toggle
              label={formatMessage(messages.setAsDefaultFilter)}
              name="setAsDefaultFilter"
              helpMessage={formatMessage(messages.setAsDefaultFilterHelp)}
              disabled={isDefaultFilterAlready && !createAsNewFilter}
            />
          }
          control={control}
          defaultValue={isDefaultFilterAlready}
          name="setAsDefaultFilter"
          value="setAsDefaultFilter"
        />
      </FormBox>
    </Modal>
  );
};
